import PropTypes from 'prop-types';
import Dotdotdot from 'react-dotdotdot';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { DropdownThreeDotsIcon } from '@hivebrite/design-system/Dropdown';
import { DefaultLink } from '@hivebrite/design-system/Link';

import {
  ACCEPTED,
  INACTIVE,
  ON_PAUSE,
  PENDING,
  REJECTED,
  TERMINATED,
  REJECTED_CAN_REAPPLY,
} from 'hb-react/frontoffice/apps/Mentorships/constants';
import { useMentorshipsContext } from 'hb-react/frontoffice/apps/Mentorships/contexts/MentorshipsContext';
import { convertMentorStatus } from 'hb-react/frontoffice/apps/Mentorships/utils';
import { completeName } from 'helpers/name';

import CustomAttributes from '../CustomAttributes';
import StatusIcon from '../StatusIcon';

const MentorshipCardBody = ({
  dropdownItems,
  mentorProfile,
  status,
  withStatusLabel,
}) => {
  const {
    user,
    locations,
    skills,
    custom_attributes: customAttributes,
  } = mentorProfile;

  const { mentorApplicationConfig } = useMentorshipsContext();

  const statusText = withStatusLabel
    ? `${s__('Mentorships|Status')}: ${convertMentorStatus(status)}`
    : convertMentorStatus(status);

  return (
    <Box flexDirection="column" flexGrow={1}>
      <Box minHeight="60px" mb="9px">
        <Box position="relative" minWidth="60px">
          <DefaultLink
            href={Routes.user_path(user.id)}
            styledOnlyOnHover
            font="h4-semibold"
          >
            <img
              className="user-item__img--rounded user-item__img--60"
              src={user.photo_url}
            />
            <StatusIcon status={status} />
          </DefaultLink>
        </Box>

        <Box flexDirection="column" ml={3} mt="10px" flex="1">
          <Box mb={1}>
            <DefaultLink
              href={Routes.user_path(user.id)}
              styledOnlyOnHover
              font="h4-semibold"
            >
              <Dotdotdot clamp={1}>{completeName(user)}</Dotdotdot>
            </DefaultLink>
          </Box>
          <Box mb={3} font="metadata-regular-asphalt">
            {statusText}
          </Box>
        </Box>
        {dropdownItems.length > 0 && (
          <Box display="block" ml={3} color="asphalt" mt={-2}>
            <DropdownThreeDotsIcon
              dataTestId="mentor-card-actions-dropdown"
              items={dropdownItems}
            />
          </Box>
        )}
      </Box>
      {mentorApplicationConfig?.mentorshipsLocationDisplayInMentorCard && (
        <Box data-testid="location-block" mt="6px" flexDirection="column">
          <Label>{s__('Mentorships|Location')}</Label>
          <Value>
            {locations[0] ? (
              <Dotdotdot clamp={1}>
                {[locations[0].city, locations[0].country]
                  .filter(Boolean)
                  .join(', ') || locations[0].address}
              </Dotdotdot>
            ) : (
              '-'
            )}
          </Value>
        </Box>
      )}
      {mentorApplicationConfig?.mentorshipsSkillsDisplayInMentorCard && (
        <Box data-testid="skills-block" mt="6px" flexDirection="column">
          <Label>{s__('Mentorships|Skills')}</Label>
          <Dotdotdot clamp={2} data-testid="skills-block-content">
            <Value>{skills?.length ? skills.join(' • ') : '-'}</Value>
          </Dotdotdot>
        </Box>
      )}

      {customAttributes && (
        <CustomAttributes customAttributes={customAttributes} />
      )}
    </Box>
  );
};

MentorshipCardBody.defaultProps = {
  dropdownItems: [],
};

MentorshipCardBody.propTypes = {
  dropdownItems: PropTypes.array,
  mentorProfile: PropTypes.object.isRequired,
  status: PropTypes.oneOf([
    INACTIVE,
    ACCEPTED,
    REJECTED,
    ON_PAUSE,
    PENDING,
    TERMINATED,
    REJECTED_CAN_REAPPLY,
  ]).isRequired,
};

export default MentorshipCardBody;

const Label = styled(Box).attrs(() => ({
  font: 'metadata-semibold-asphalt',
  fontSize: 1,
  mr: 1,
  as: 'span',
  display: 'inline',
}))``;

const Value = styled(Box).attrs(() => ({
  font: 'metadata-regular-asphalt',
  fontSize: 1,
  mr: 1,
  as: 'span',
  display: 'inline',
}))``;
